import { assign, fromPromise, setup } from 'xstate';
import { showAxiosErrorAction } from '../../actions';
import { FacilityFormValues } from '../../types';
import {
  clearDuplicates,
  closeModal,
  notifyParentOnClose,
  notifyParentOnCreation,
  notifyParentOnOpen,
  notifyParentOnSelection,
  openModal,
  resetForm,
  resetLoading,
  resetOnError,
  seedFormName,
  selectExistingFacility,
  setDuplicatesFound,
  setLoading,
  setParentId,
  setPendingValues,
  showDuplicatesModal,
  updateFormOnSuccess,
} from './actions';
import { API_CALLS } from './api';
import { emptyFacilityForm } from './constants';
import { Events, SubmitFacilityEvent } from './events';
import { Context } from './types';

export const facilityMachine = setup({
  actors: {
    createFacility: fromPromise(API_CALLS.create),
    checkDuplicates: fromPromise(
      async ({ input }: { input: { name: string } }): Promise<FacilityFormValues[]> => {
        if (!input.name.trim()) return [];
        return await API_CALLS.get(input.name.trim());
      }
    ),
  },
  types: {
    context: {} as Context,
    events: {} as Events,
  },
  actions: {
    setParentId: assign(setParentId),
    openModal: assign(openModal),
    closeModal: assign(closeModal),
    setLoading: assign(setLoading),
    resetLoading: assign(resetLoading),
    updateFormOnSuccess: assign(updateFormOnSuccess),
    resetOnError: assign(resetOnError),
    sendError: showAxiosErrorAction,
    notifyParentOnCreation,
    notifyParentOnOpen,
    notifyParentOnClose,
    notifyParentOnSelection,
    resetForm: assign(resetForm),
    setDuplicatesFound: assign(setDuplicatesFound),
    clearDuplicates: assign(clearDuplicates),
    setPendingValues: assign(setPendingValues),
    seedFormName: assign(seedFormName),
    selectExistingFacility: assign(selectExistingFacility),
    showDuplicatesModal: assign(showDuplicatesModal),
  },
  guards: {
    isInline: ({ context }) => !!context.inline,
    hasDuplicates: ({ event }) => {
      const { output } = event as unknown as { output: FacilityFormValues[] };
      return output.length > 0;
    },
  },
}).createMachine({
  context: {
    loading: false,
    modal: false,
    form: emptyFacilityForm,
    parentId: '',
    duplicates: [],
    pendingValues: emptyFacilityForm,
    showDuplicates: false,
  },
  initial: '$_CLOSED',
  entry: ['setParentId'],
  states: {
    $_OPENED: {
      entry: ['openModal', 'notifyParentOnOpen'],
      on: {
        ON_SUBMIT: {
          target: '$_CHECK_DUPLICATES',
          actions: ['setPendingValues'],
        },
        ON_CLOSE: {
          target: '$_CLOSED',
        },
      },
    },
    $_CLOSED: {
      entry: ['closeModal', 'notifyParentOnClose', 'resetForm', 'clearDuplicates'],
      on: {
        ON_OPEN: {
          target: '$_OPENED',
          actions: [assign({ inline: false, inlineError: undefined }), 'seedFormName'],
        },
        // Training owns the visible fields; only duplicate checking and saving
        // run here. Existing modal consumers continue to use ON_OPEN/ON_SUBMIT.
        ON_SUBMIT_INLINE: {
          target: '$_CHECK_DUPLICATES',
          actions: [assign({ inline: true, inlineError: undefined }), 'setPendingValues'],
        },
      },
    },
    $_CHECK_DUPLICATES: {
      entry: ['setLoading'],
      invoke: {
        id: 'checkDuplicates',
        src: 'checkDuplicates',
        input: ({ context }) => ({ name: context.pendingValues.label }),
        onDone: [
          {
            guard: 'hasDuplicates',
            target: '$_SHOW_DUPLICATES',
            actions: ['setDuplicatesFound', 'showDuplicatesModal'],
          },
          {
            target: '$_SEND_CREATE_FACILITY',
          },
        ],
        onError: [
          {
            guard: 'isInline',
            target: '$_CLOSED',
            actions: [
              'resetLoading',
              assign({ inlineError: 'Unable to check for similar facilities. Please try again.' }),
            ],
          },
          { target: '$_SEND_CREATE_FACILITY' },
        ],
      },
    },
    $_SHOW_DUPLICATES: {
      entry: ['resetLoading'],
      on: {
        ON_CONFIRM_CREATE: {
          target: '$_SEND_CREATE_FACILITY',
        },
        ON_SELECT_EXISTING: {
          target: '$_CLOSED',
          actions: ['selectExistingFacility', 'notifyParentOnSelection'],
        },
        ON_BACK_TO_FORM: [
          { guard: 'isInline', target: '$_CLOSED' },
          { target: '$_OPENED', actions: ['clearDuplicates'] },
        ],
        ON_CLOSE: {
          target: '$_CLOSED',
        },
      },
    },
    $_SEND_CREATE_FACILITY: {
      entry: [
        'setLoading',
        assign({
          showDuplicates: ({ context }) => (context.inline ? false : context.showDuplicates),
        }),
      ],
      invoke: {
        id: 'create',
        src: 'createFacility',
        input: ({ context }) =>
          ({ context, event: { type: 'ON_SUBMIT', values: context.pendingValues } }) as {
            context: Context;
            event: SubmitFacilityEvent;
          },
        onDone: {
          target: '$_CLOSED',
          actions: ['updateFormOnSuccess', 'notifyParentOnCreation'],
        },
        onError: [
          {
            guard: 'isInline',
            target: '$_CLOSED',
            actions: [
              'resetLoading',
              assign({ inlineError: 'Unable to save the facility. Please try again.' }),
            ],
          },
          { target: '$_CLOSED', actions: ['sendError', 'resetOnError'] },
        ],
      },
    },
  },
});
